import '../link-context-menu.css';

interface LinkEntry {
  link: HTMLAnchorElement;
  href: string;
  attributes: Map<string, string | null>;
}

interface NavigationEntry {
  link: HTMLAnchorElement;
  href: string;
  restoreHref: string | null;
  attributes: Map<string, string | null>;
  applied: Map<string, string | null>;
}

// Match the v2 workspaces: retain the destination in memory and remove href
// after mounting so hovering has no browser URL preview. Authored hrefs work
// when this enhancement is absent and are restored when it is removed.
export function installLinkContextMenu(document: Document = window.document, navigate?: (href: string) => boolean): () => void {
  const view = document.defaultView;
  if (!view) return () => {};

  const menu = document.createElement('div');
  menu.className = 'tp-link-menu';
  menu.id = 'tp-link-context-menu';
  menu.hidden = true;
  menu.setAttribute('role', 'menu');
  const items = ['Open in new tab', 'Copy link'].map((label) => {
    const button = document.createElement('button');
    button.className = 'tp-link-menu-item';
    button.type = 'button';
    button.tabIndex = -1;
    button.setAttribute('role', 'menuitem');
    button.textContent = label;
    menu.append(button);
    return button;
  });
  const feedback = document.createElement('div');
  feedback.className = 'tp-link-feedback';
  feedback.setAttribute('role', 'status');
  feedback.setAttribute('aria-live', 'polite');
  feedback.setAttribute('aria-atomic', 'true');
  feedback.hidden = true;
  document.body.append(menu, feedback);

  let active: LinkEntry | null = null;
  let feedbackTimer: number | undefined;
  let destroyed = false;
  const links = new Map<HTMLAnchorElement, NavigationEntry>();
  const clicks = new WeakMap<Event, { href: string; finished: boolean }>();
  const listeners: (() => void)[] = [];
  function listen(target: EventTarget, type: string, handler: EventListener, capture = false) {
    target.addEventListener(type, handler, capture);
    listeners.push(() => target.removeEventListener(type, handler, capture));
  }
  function follow(href: string) {
    // Public React routes can handle ordinary activation in the current
    // document. Other destinations retain the existing browser navigation.
    if (navigate?.(href) !== true) view!.location.assign(href);
  }
  function eligible(link: HTMLAnchorElement, href: string) {
    const targetName = link.getAttribute('target')?.toLowerCase();
    if (!href.trim() || href.trim() === '#' || link.hasAttribute('download')
      || link.matches('.skip-link, .m-skip-link, .disabled, [aria-disabled="true"]')
      || link.closest('[inert], [aria-hidden="true"]')
      || (targetName && !['_self', '_blank'].includes(targetName))) return false;
    try { return ['http:', 'https:'].includes(new URL(href, document.baseURI).protocol); }
    catch { return false; }
  }
  function restore(entry: NavigationEntry, forget = true) {
    for (const [name, value] of entry.attributes) {
      if (entry.link.getAttribute(name) === value) continue;
      if (value === null) entry.link.removeAttribute(name);
      else entry.link.setAttribute(name, value);
    }
    if (entry.restoreHref === null) entry.link.removeAttribute('href');
    else if (entry.link.getAttribute('href') !== entry.restoreHref) entry.link.setAttribute('href', entry.restoreHref);
    entry.applied.clear();
    if (forget) links.delete(entry.link);
  }
  function enhance(link: HTMLAnchorElement): NavigationEntry | null {
    let entry = links.get(link);
    const authoredHref = link.getAttribute('href');
    if (entry) {
      if (authoredHref !== null) entry.href = authoredHref;
      for (const name of ['role', 'tabindex']) {
        const value = link.getAttribute(name);
        if (!entry.applied.has(name) || value !== entry.applied.get(name)) entry.attributes.set(name, value);
      }
    }
    const href = authoredHref ?? entry?.href;
    if (href === undefined || href === null || !eligible(link, href)) {
      if (entry) {
        if (active?.link === link) close();
        const disabled = link.matches('.disabled, [aria-disabled="true"]');
        // Bootstrap removes href when disabling an anchor. Keep it absent,
        // while retaining the saved destination for a later re-enabled node.
        entry.restoreHref = disabled ? authoredHref : entry.href;
        restore(entry, !disabled);
      }
      return null;
    }
    if (!entry) {
      entry = { link, href, restoreHref: href, attributes: new Map(), applied: new Map() };
      for (const name of ['role', 'tabindex', 'data-tp-navigation']) entry.attributes.set(name, link.getAttribute(name));
      links.set(link, entry);
    }
    entry.restoreHref = href;
    // Preserve roles and roving tab indices authored by Bootstrap/React, also
    // when React updates a retained node after the initial enhancement.
    for (const [name, fallback] of [['role', 'link'], ['tabindex', '0']] as const) {
      const value = link.getAttribute(name);
      const applied = value ?? fallback;
      if (value !== applied) link.setAttribute(name, applied);
      entry.applied.set(name, applied);
    }
    if (!link.hasAttribute('data-tp-navigation')) link.setAttribute('data-tp-navigation', '');
    if (authoredHref !== null) link.removeAttribute('href');
    if (active?.link === link) active.href = new URL(entry.href, document.baseURI).href;
    return entry;
  }
  function refresh() {
    if (destroyed) return;
    if (active && !active.link.isConnected) close();
    for (const entry of links.values()) {
      if (!entry.link.isConnected) restore(entry);
      else enhance(entry.link);
    }
    for (const link of document.querySelectorAll<HTMLAnchorElement>('a[href]')) enhance(link);
  }
  function readLink(target: EventTarget | null): NavigationEntry | null {
    const element = target instanceof view!.Element ? target : target instanceof view!.Node ? target.parentElement : null;
    const link = element?.closest<HTMLAnchorElement>('a');
    if (!link || !document.contains(link)) return null;
    return enhance(link);
  }
  function close(restoreFocus = false) {
    const entry = active;
    if (!entry) return;
    active = null;
    items.forEach((item) => { item.tabIndex = -1; });
    for (const [name, value] of entry.attributes) {
      if (value === null) entry.link.removeAttribute(name);
      else entry.link.setAttribute(name, value);
    }
    // Return focus before hiding a focused menu so dismissal keeps a stable
    // focus target inside the surrounding dialog or navigation widget.
    if (restoreFocus && entry.link.isConnected) entry.link.focus();
    menu.hidden = true;
  }
  function focusItem(index: number) {
    items.forEach((item, candidate) => { item.tabIndex = candidate === index ? 0 : -1; });
    items[index].focus();
  }
  function show(navigation: NavigationEntry, event: MouseEvent | KeyboardEvent) {
    close();
    const { link } = navigation;
    const entry: LinkEntry = {
      link,
      href: new URL(navigation.href, document.baseURI).href,
      attributes: new Map(['aria-haspopup', 'aria-expanded', 'aria-controls', 'aria-keyshortcuts']
        .map((name) => [name, link.getAttribute(name)])),
    };
    const rect = link.getBoundingClientRect();
    const pointer = event instanceof view!.MouseEvent && (event.clientX || event.clientY);
    const x = pointer ? event.clientX : rect.left;
    const y = pointer ? event.clientY : rect.bottom;
    // Mount in the modal surface before considering a search wrapper. This
    // keeps the menu within the focus trap and outside scrollable result panes.
    const parent = link.closest<HTMLElement>('[aria-modal="true"], dialog[open]')
      ?? link.closest<HTMLElement>('.search-shell') ?? document.body;
    parent.append(menu, feedback);
    const label = link.getAttribute('aria-label') || link.querySelector('h2, h3')?.textContent || link.textContent || 'this link';
    menu.setAttribute('aria-label', `Link options for ${label.trim().replace(/\s+/g, ' ') || 'this link'}`);
    menu.style.left = '0px';
    menu.style.top = '0px';
    menu.hidden = false;
    const bounds = menu.getBoundingClientRect();
    const width = document.documentElement.clientWidth || view!.innerWidth;
    const height = document.documentElement.clientHeight || view!.innerHeight;
    // Measuring the zero position also handles containing blocks introduced
    // by offcanvas transforms without guessing the parent's CSS positioning.
    menu.style.left = `${Math.max(8, Math.min(x, width - bounds.width - 8)) - bounds.left}px`;
    menu.style.top = `${Math.max(8, Math.min(y, height - bounds.height - 8)) - bounds.top}px`;
    active = entry;
    link.setAttribute('aria-haspopup', 'menu');
    link.setAttribute('aria-expanded', 'true');
    link.setAttribute('aria-controls', menu.id);
    link.setAttribute('aria-keyshortcuts', 'Shift+F10');
    focusItem(0);
  }
  listen(document, 'contextmenu', (event) => {
    if (event.defaultPrevented) return;
    const link = readLink(event.target);
    if (!link) { close(); return; }
    event.preventDefault();
    show(link, event as MouseEvent);
  });
  listen(document, 'click', (event) => {
    const click = event as MouseEvent;
    if (click.defaultPrevented || click.button !== 0) return;
    const entry = readLink(click.target);
    if (!entry) return;
    const href = new URL(entry.href, document.baseURI).href;
    if (click.ctrlKey || click.metaKey || click.shiftKey || entry.link.getAttribute('target')?.toLowerCase() === '_blank') {
      click.preventDefault();
      click.stopPropagation();
      view.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    const activation = { href, finished: false };
    clicks.set(click, activation);
    // A handler may stop propagation while still leaving navigation enabled.
    // Check once it has run, even when the document bubble listener is skipped.
    view.queueMicrotask(() => {
      if (destroyed || activation.finished || click.defaultPrevented) return;
      activation.finished = true;
      follow(href);
    });
  }, true);
  listen(document, 'click', (event) => {
    const activation = clicks.get(event);
    if (!activation || activation.finished) return;
    activation.finished = true;
    // React search and custom selection handlers own their prevented clicks.
    if (event.defaultPrevented) return;
    event.preventDefault();
    follow(activation.href);
  });
  listen(document, 'mousedown', (event) => {
    if ((event as MouseEvent).button === 1 && readLink(event.target)) event.preventDefault();
  }, true);
  listen(document, 'auxclick', (event) => {
    if ((event as MouseEvent).button !== 1 || event.defaultPrevented) return;
    const entry = readLink(event.target);
    if (!entry) return;
    event.preventDefault();
    event.stopPropagation();
    view.open(new URL(entry.href, document.baseURI).href, '_blank', 'noopener,noreferrer');
  }, true);
  listen(document, 'keydown', (event) => {
    const key = event as KeyboardEvent;
    if (key.defaultPrevented || key.isComposing || key.repeat || key.key !== 'Enter' || !(key.ctrlKey || key.metaKey || key.shiftKey)) return;
    const entry = readLink(key.target);
    if (!entry) return;
    key.preventDefault();
    key.stopPropagation();
    view.open(new URL(entry.href, document.baseURI).href, '_blank', 'noopener,noreferrer');
  }, true);
  listen(document, 'keydown', (event) => {
    const key = event as KeyboardEvent;
    if (key.defaultPrevented || key.isComposing || key.repeat || key.altKey || key.ctrlKey || key.metaKey) return;
    if (key.key !== 'Enter' && key.key !== 'ContextMenu' && !(key.shiftKey && key.key === 'F10')) return;
    const link = readLink(event.target);
    if (!link) return;
    key.preventDefault();
    if (key.key === 'Enter') {
      link.link.dispatchEvent(new view.MouseEvent('click', { bubbles: true, cancelable: true, view, button: 0 }));
      return;
    }
    key.stopPropagation();
    show(link, key);
  });
  listen(menu, 'keydown', (event) => {
    const key = event as KeyboardEvent;
    if (key.isComposing || key.altKey || key.ctrlKey || key.metaKey) return;
    const current = items.findIndex((item) => item === document.activeElement);
    let next = -1;
    if (key.key === 'ArrowDown') next = (current + 1) % items.length;
    else if (key.key === 'ArrowUp') next = (current + items.length - 1) % items.length;
    else if (key.key === 'Home') next = 0;
    else if (key.key === 'End') next = items.length - 1;
    else if (key.key === 'Escape') {
      key.preventDefault();
      key.stopPropagation();
      close(true);
    } else if (key.key === 'Tab') close(true);
    else if (key.key.length === 1 && /\S/.test(key.key)) next = items.findIndex((item) => item.textContent!.toLowerCase().startsWith(key.key.toLowerCase()));
    if (next >= 0) {
      key.preventDefault();
      key.stopPropagation();
      focusItem(next);
    }
  });
  listen(items[0], 'click', () => {
    const entry = active;
    if (!entry) return;
    close(true);
    view.open(entry.href, '_blank', 'noopener,noreferrer');
  });
  listen(items[1], 'click', async () => {
    const entry = active;
    if (!entry) return;
    try {
      if (!view.navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await view.navigator.clipboard.writeText(entry.href);
      if (destroyed) return;
      if (active === entry) close(true);
      view.clearTimeout(feedbackTimer);
      feedback.textContent = 'Link copied.';
      feedback.hidden = false;
      feedbackTimer = view.setTimeout(() => { feedback.hidden = true; }, 2500);
    } catch {
      if (destroyed) return;
      if (active === entry) close(true);
      view.prompt('Copy link:', entry.href);
    }
  });
  listen(document, 'pointerdown', (event) => { if (!menu.contains(event.target as Node)) close(); });
  listen(document, 'focusin', (event) => { if (!menu.contains(event.target as Node)) close(); });
  listen(document, 'scroll', (event) => { if (!menu.contains(event.target as Node)) close(true); }, true);
  listen(view, 'resize', () => close(true));
  listen(view, 'blur', () => close());
  const observer = new view.MutationObserver(refresh);
  observer.observe(document.body, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['href', 'target', 'download', 'class', 'aria-disabled', 'aria-hidden', 'inert', 'role', 'tabindex'],
  });
  refresh();

  return () => {
    refresh();
    destroyed = true;
    close(true);
    observer.disconnect();
    listeners.forEach((remove) => remove());
    view.clearTimeout(feedbackTimer);
    for (const entry of links.values()) restore(entry);
    menu.remove();
    feedback.remove();
  };
}
